import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import {
  createPassword,
  fetchSignInMethod,
  syncPasskeys,
  updateSignInMethod,
} from "../helpers/apiHelper";
import { getPasswordRules, isPasswordValid } from "../helpers/validationHelper";
import { SELECTABLE_SIGN_IN_METHODS, SIGN_IN_METHOD_MODES } from "../helpers/signInModes";
import PasskeySettings from "./PasskeySettings";

// Profile "Forma de entrar": email code only or password + email code,
// plus the Face ID / biometrics (passkey) setting for this device.
// Password changes need a recent sign-in; the backend answers 401 REAUTH_REQUIRED otherwise.
const SignInMethodSettings = () => {
  const { tokens, signOut } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const idToken = tokens?.idToken;
  const accessToken = tokens?.accessToken;

  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [reauthRequired, setReauthRequired] = useState(false);
  // Face ID accounts have no password: while passkeys exist, password + code is unavailable.
  // The backend says so via passkeyRequiresCodeOnly; the local list covers the gap until it answers.
  const [passkeyCount, setPasskeyCount] = useState(0);
  const hasPasskeys = Boolean(status?.passkeyRequiresCodeOnly) || passkeyCount > 0;

  // { method } when the password form is open; method is sent with POST /users/me/password.
  const [passwordForm, setPasswordForm] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const passwordRules = getPasswordRules(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmNewPassword;
  const canSubmitPassword = isPasswordValid(newPassword) && passwordsMatch;

  const applyStatus = useCallback((next) => {
    setStatus(next);
    // A method we no longer offer (legacy PASSWORD_ONLY) leaves nothing selected.
    setSelected(SELECTABLE_SIGN_IN_METHODS.includes(next.method) ? next.method : null);
  }, []);

  // Syncing first lets accounts that registered a passkey before the Face ID rule fix themselves.
  const syncOrFetch = useCallback(async () => {
    if (accessToken) {
      try {
        return await syncPasskeys(idToken, accessToken);
      } catch (err) {
        console.warn("Passkey sync failed", err?.status, err?.code);
      }
    }
    return fetchSignInMethod(idToken);
  }, [idToken, accessToken]);

  const load = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    try {
      applyStatus(await syncOrFetch());
    } catch (err) {
      console.warn("Could not load the sign-in method", err?.status);
      setError("signInMethod.loadFailed");
    } finally {
      setLoading(false);
    }
  }, [idToken, syncOrFetch, applyStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFailure = (err, fallback) => {
    if (err?.code === "REAUTH_REQUIRED") {
      setPasswordForm(null);
      setReauthRequired(true);
      return;
    }
    if (err?.code === "PASSKEY_ACTIVE") {
      setPasswordForm(null);
      setError("signInMethod.passkeyActive");
      return;
    }
    if (err?.code === "PASSWORD_REQUIRED") {
      openPasswordForm(selected);
      setInfo("signInMethod.passwordRequired");
      return;
    }
    setError(fallback);
  };

  const openPasswordForm = (method) => {
    setError("");
    setInfo("");
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordForm({ method });
  };

  const handleSave = async () => {
    if (!selected || selected === status?.method || saving) return;
    setError("");
    setInfo("");

    if (selected === SIGN_IN_METHOD_MODES.PASSWORD_AND_CODE && !status?.hasPassword) {
      openPasswordForm(selected);
      setInfo("signInMethod.passwordRequired");
      return;
    }

    setSaving(true);
    try {
      const next = await updateSignInMethod(selected, idToken);
      if (next) applyStatus(next);
      else setStatus((prev) => ({ ...prev, method: selected }));
      setInfo("signInMethod.saved");
    } catch (err) {
      handleFailure(err, "signInMethod.saveFailed");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmitPassword || saving) return;
    setError("");
    setInfo("");
    setSaving(true);
    try {
      await createPassword(newPassword, idToken, passwordForm?.method || undefined);
      setPasswordForm(null);
      setNewPassword("");
      setConfirmNewPassword("");
      setInfo("signInMethod.passwordSaved");
      await load();
    } catch (err) {
      handleFailure(
        err,
        err?.code === "INVALID_PASSWORD" ? "signIn.createPasswordInvalid" : "signInMethod.passwordFailed",
      );
    } finally {
      setSaving(false);
    }
  };

  // Called by the Face ID toggle after the user confirms; resolves true when the account is CODE_ONLY.
  const switchToCodeOnly = async () => {
    setError("");
    setInfo("");
    setPasswordForm(null);
    try {
      const next = await updateSignInMethod(SIGN_IN_METHOD_MODES.CODE_ONLY, idToken);
      applyStatus(next || { ...status, method: SIGN_IN_METHOD_MODES.CODE_ONLY });
      return true;
    } catch (err) {
      handleFailure(err, "signInMethod.saveFailed");
      return false;
    }
  };

  const handlePasskeysChange = useCallback((list) => setPasskeyCount(list.length), []);

  // After a register/delete the backend removes the password or restores email MFA.
  const handlePasskeysMutated = useCallback(async () => {
    try {
      applyStatus(await syncOrFetch());
    } catch (err) {
      console.warn("Could not reload the sign-in method", err?.status);
    }
  }, [syncOrFetch, applyStatus]);

  const handleReauth = async () => {
    await signOut({ reason: "REAUTH_REQUIRED" });
    navigate("/signin", { replace: true });
  };

  // Keeps password + code when changing the password; otherwise the backend default.
  const passwordMethodForChange =
    status?.method === SIGN_IN_METHOD_MODES.PASSWORD_AND_CODE ? status.method : undefined;

  return (
    <section
      className="portal-page-panel signin-method-settings"
      aria-busy={loading || saving}
      aria-labelledby="signin-method-title"
    >
      <h3 id="signin-method-title" className="portal-label">{t("signInMethod.title")}</h3>
      <p>{t("signInMethod.description")}</p>

      {loading && <p role="status">{t("signInMethod.loading")}</p>}
      {error && <p className="error" role="alert">{t(error)}</p>}
      {info && <p className="success" role="status">{t(info)}</p>}

      {reauthRequired && (
        <div className="signin-method-reauth" role="alert">
          <p>{t("signInMethod.reauthRequired")}</p>
          <button type="button" className="portal-button" onClick={handleReauth}>
            {t("signInMethod.reauthButton")}
          </button>
        </div>
      )}

      {status && (
        <>
          {!selected && (
            <p className="signin-method-choose" role="status">{t("signInMethod.chooseMethod")}</p>
          )}
          <fieldset className="signin-method-options" disabled={saving}>
            <legend className="visually-hidden">{t("signInMethod.title")}</legend>
            {SELECTABLE_SIGN_IN_METHODS.map((value) => (
              <label key={value} className="signin-method-option">
                <input
                  type="radio"
                  name="sign-in-method"
                  value={value}
                  checked={selected === value}
                  onChange={() => setSelected(value)}
                  disabled={hasPasskeys && value === SIGN_IN_METHOD_MODES.PASSWORD_AND_CODE}
                />
                <span>{t(`signInMethod.option.${value}`)}</span>
              </label>
            ))}
          </fieldset>
          {hasPasskeys && <p className="signin-method-hint">{t("signInMethod.passkeyActive")}</p>}

          <div className="portal-inline-form-controls signin-method-actions">
            <button
              type="button"
              className={`portal-button ${saving || !selected || selected === status.method ? "disabled" : ""}`}
              onClick={handleSave}
              disabled={saving || !selected || selected === status.method}
            >
              {saving && !passwordForm ? t("updating") : t("signInMethod.save")}
            </button>
            {!passwordForm && (
              <button
                type="button"
                className="portal-button"
                onClick={() => openPasswordForm(passwordMethodForChange)}
                disabled={saving || hasPasskeys}
              >
                {t(status.hasPassword ? "signInMethod.changePassword" : "signInMethod.createPassword")}
              </button>
            )}
          </div>
        </>
      )}

      {passwordForm && (
        <form className="signin-method-password" onSubmit={handlePasswordSubmit} noValidate>
          <div className="password-field">
            <input
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("signIn.newPasswordPlaceholder")}
              aria-label={t("signIn.newPasswordPlaceholder")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="portal-input"
            />
            <span
              className="password-toggle-icon"
              onClick={() => setShowNewPassword((prev) => !prev)}
              role="button"
              aria-label={showNewPassword ? t("signInMethod.hidePassword") : t("signInMethod.showPassword")}
            >
              {showNewPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          <div className="password-field">
            <input
              type={showConfirmNewPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder={t("signIn.confirmNewPasswordPlaceholder")}
              aria-label={t("signIn.confirmNewPasswordPlaceholder")}
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              className="portal-input"
            />
            <span
              className="password-toggle-icon"
              onClick={() => setShowConfirmNewPassword((prev) => !prev)}
              role="button"
              aria-label={showConfirmNewPassword ? t("signInMethod.hidePassword") : t("signInMethod.showPassword")}
            >
              {showConfirmNewPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          {confirmNewPassword.length > 0 && (
            <p className={`password-match ${passwordsMatch ? "valid" : "invalid"}`}>
              {passwordsMatch ? t("signIn.passwordsMatch") : t("signIn.passwordsDoNotMatch")}
            </p>
          )}

          <ul className="signin-method-rules">
            <li className={passwordRules.minLength ? "valid" : ""}>{t("signIn.newPasswordLine1")}</li>
            <li className={passwordRules.hasNumber ? "valid" : ""}>{t("signIn.newPasswordLine2")}</li>
            <li className={passwordRules.hasSpecialChar ? "valid" : ""}>{t("signIn.newPasswordLine3")}</li>
            <li className={passwordRules.hasUppercase ? "valid" : ""}>{t("signIn.newPasswordLine4")}</li>
            <li className={passwordRules.hasLowercase ? "valid" : ""}>{t("signIn.newPasswordLine5")}</li>
          </ul>

          <div className="portal-inline-form-controls">
            <button
              type="submit"
              className={`portal-button ${saving || !canSubmitPassword ? "disabled" : ""}`}
              disabled={saving || !canSubmitPassword}
            >
              {saving ? t("updating") : t("signInMethod.savePassword")}
            </button>
            <button
              type="button"
              className="portal-button"
              onClick={() => setPasswordForm(null)}
              disabled={saving}
            >
              {t("signInMethod.cancel")}
            </button>
          </div>
        </form>
      )}

      <PasskeySettings
        embedded
        requiresCodeOnlySwitch={Boolean(
          status && (status.hasPassword || status.method !== SIGN_IN_METHOD_MODES.CODE_ONLY),
        )}
        onSwitchToCodeOnly={switchToCodeOnly}
        onPasskeysChange={handlePasskeysChange}
        onPasskeysMutated={handlePasskeysMutated}
      />
    </section>
  );
};

export default SignInMethodSettings;
